<script setup lang="ts">
import { computed } from '#imports';
import { useClipboard } from '@vueuse/core';

import { UiIconButton, UiIconName } from '../icon';
import { SpecialSymbols } from '../../utils/specialSymbols';
import { useI18n } from 'vue-i18n';

const props = withDefaults(defineProps<{
  locale?: string;
  text?: string;
  items?: { name: string; value: string }[];
  hide?: string[];
}>(), {
  locale: '',
  text: '',
  items: () => [],
  hide: () => [],
});

const i18n = useI18n();

const localizedItems = computed(() =>
  props.items
    .map((item) => ({
      key: item.name,
      name: i18n.t(item.name || ''),
      value: i18n.t(item.value || '') || SpecialSymbols.EmptyDash,
    }))
    .filter((item) => !props.hide.includes(item.key)),
);

const { copy } = useClipboard();

const emit = defineEmits(['copy']);

function onCopy() {
  copy(props.text || localizedItems.value.map((el) => `${el.name}: ${el.value}`).join('\n'));
  emit('copy');
}
</script>

<template lang="pug">
.ui-copy-container
  .ui-copy-container_header.flex.row-8
    .ui-copy-container_header_title
      slot(name="header")
        | {{ $t(locale) }}
    .flex.row-8
      slot(name="actions")
      UiIconButton.copy-btn(
        tooltip="Copy"
        :name="UiIconName.FileAndFolder_DocumentCopy"
        @click="onCopy"
      )
  slot
    .ui-copy-container_item(v-for="item in localizedItems" :key="item.key")
      .name {{ item.name }}
      .value
        slot(:name="item.key" :value="item.value") {{ item.value }}
</template>

<style lang="scss" scoped>
@import '../../styles/typography';
@import '../../styles/colors';

.ui-copy-container {
  padding: 16px;
  position: relative;
  max-width: 600px;

  .ui-copy-container_item {
    @include ui-typography-S-1;
    display: flex;
    justify-content: space-between;
    white-space: break-spaces;

    &:not(:last-child) {
      margin-bottom: 8px;
    }

    .name {
      min-width: max-content;
      color: $ui-color-text-secondary;
    }

    .value {
      margin-left: 16px;
      color: $ui-color-text-default;
    }
  }
}

.ui-copy-container_header {
  @include ui-typography-L-2;
  justify-content: space-between;
  padding-bottom: 12px;
  color: $ui-color-text-default;
}

.ui-copy-container_header_title {
  width: 100%;
  min-height: 24px;
}

.copy-btn {
  color: $ui-color-icon-default;
}
</style>
